<template>
  <div class="classifications-modern">
    <!-- Modals -->
    <NewClassification @created="getClassifications" />
    <ViewClassification
      :classification="classification"
      @updated="getClassifications"
      @close="classification = null"
    />

    <div class="classifications-container">
      <!-- Header Section -->
      <div class="classifications-header">
        <div class="header-content">
          <div class="title-section">
            <h1 class="page-title">
              <i class="fas fa-tags title-icon"></i>
              Classificações
            </h1>
            <p class="page-subtitle">
              Gerencie as classificações de fornecedores
            </p>
          </div>

          <div class="actions-section">
            <v-btn
              v-if="permission('write', 'financial_provider')"
              @click="$bvModal.show('new_classification')"
              class="btn-add-modern"
            >
              <i class="fas fa-plus mr-2"></i>
              Adicionar
            </v-btn>
          </div>
        </div>
      </div>

      <!-- Content Section -->
      <div class="classifications-content">
        <!-- Filters Section -->
        <div class="filters-section">
          <div class="filters-card">
            <div class="filters-grid">
              <!-- Status Filter -->
              <div class="filter-group">
                <label class="filter-label">
                  <i class="fas fa-toggle-on filter-icon"></i>
                  Status
                </label>
                <select
                  v-model="active"
                  class="modern-form-select"
                  @change="getClassifications"
                >
                  <option
                    v-for="(item, key) in active_filters"
                    :key="key"
                    :value="item.value"
                  >
                    {{ item.text }}
                  </option>
                </select>
              </div>

              <!-- Search -->
              <div class="filter-group search-group">
                <label class="filter-label">
                  <i class="fas fa-search filter-icon"></i>
                  Pesquisar
                </label>
                <input
                  v-model="search"
                  type="text"
                  class="modern-form-input"
                  placeholder="Digite o nome da classificação"
                />
              </div>
            </div>
          </div>
        </div>

        <!-- Table Section -->
        <div class="table-section">
          <div class="table-card">
            <b-overlay :show="loading" opacity="0.99" class="loading-overlay">
              <!-- No Results State -->
              <div v-show="classifications.length === 0" class="no-results">
                <i class="fas fa-inbox no-results-icon"></i>
                <h3 class="no-results-title">
                  Nenhuma classificação encontrada
                </h3>
                <p class="no-results-text">
                  {{
                    search
                      ? "Tente ajustar os filtros para encontrar resultados"
                      : "Adicione uma nova classificação para começar"
                  }}
                </p>
              </div>

              <!-- Data Table -->
              <v-data-table
                v-show="classifications.length > 0"
                :headers="headers"
                :items="classifications"
                :search="search"
                :footer-props="{
                  'items-per-page-options': [50, 75, 100],
                  'items-per-page-text': 'Mostrar',
                }"
                class="modern-table"
                :no-data-text="'Nenhuma classificação encontrada'"
                :loading-text="'Carregando classificações...'"
              >
                <!-- Name Column -->
                <template v-slot:[`item.name`]="{ item }">
                  <div class="name-cell">
                    <span class="name-text">{{ item.name }}</span>
                  </div>
                </template>

                <!-- Active Column -->
                <template v-slot:[`item.active`]="{ item }">
                  <span
                    class="status-badge"
                    :class="item.active ? 'status-active' : 'status-inactive'"
                  >
                    <i
                      :class="[
                        'fas',
                        item.active ? 'fa-check-circle' : 'fa-times-circle',
                        'mr-1',
                      ]"
                    ></i>
                    {{ item.active ? "ATIVO" : "INATIVO" }}
                  </span>
                </template>

                <!-- Actions Column -->
                <template v-slot:[`item.actions`]="{ item }">
                  <v-btn
                    small
                    @click="classification = item"
                    class="btn-action-edit"
                  >
                    <i class="fas fa-edit mr-1"></i>
                    Editar
                  </v-btn>
                </template>
              </v-data-table>

              <!-- Errors -->
              <ErrorsMessages :errors="errors" />
            </b-overlay>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import NewClassification from "./NewClassification.vue";
import ViewClassification from "./ViewClassification.vue";
import ErrorsMessages from "../../Components/ErrorsMessages.vue";
import permission from "../../../lib/permission";
import api from "../../../services/api";
import { getRestHomeSelectedFinancialAccount } from "../../../lib/functions";

export default {
  name: "Classifications",

  components: { NewClassification, ViewClassification, ErrorsMessages },

  data() {
    return {
      errors: [],
      loading: false,

      active_filters: [
        { text: "ATIVO", value: "1" },
        { text: "INATIVO", value: "0" },
      ],
      active: "1",

      search: "",
      headers: [
        { text: "NOME", value: "name" },
        { text: "STATUS", value: "active" },
        { text: "AÇÕES", value: "actions", sortable: false },
      ],
      classifications: [],

      classification: null,
    };
  },

  created() {
    this.getClassifications();
  },

  methods: {
    getRestHomeSelectedFinancialAccount,
    permission,

    async getClassifications() {
      try {
        this.loading = true;

        let rest_home_id_selected_financial_account =
          this.getRestHomeSelectedFinancialAccount();

        const params = {
          rest_home_id_selected_financial_account,
          active: this.active,
        };

        const response = await api.get("/rest-home/providers/classifications", {
          params,
        });

        const { error, errors, classifications } = response.data;

        if (error) {
          this.errors = errors;
        } else {
          this.errors = [];
          this.classifications = classifications;
        }
      } catch (error) {
        console.log(error);
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style scoped>
.classifications-modern {
  min-height: 100vh;
  background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
}

.classifications-container {
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 24px;
}

/* Header Section */
.classifications-header {
  padding: 24px 0;
  background: white;
  border-radius: 0 0 24px 24px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  margin-bottom: 32px;
}

.header-content {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 24px;
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 24px;
}

.title-section {
  flex: 1;
}

.page-title {
  font-size: 32px;
  font-weight: 700;
  color: #1e293b;
  margin: 0;
  display: flex;
  align-items: center;
  font-family: "WorkSans-SemiBold", sans-serif;
}

.title-icon {
  color: #0f65b0;
  margin-right: 12px;
}

.page-subtitle {
  color: #64748b;
  margin: 4px 0 0 0;
  font-size: 16px;
}

/* Buttons */
.btn-add-modern {
  background: linear-gradient(135deg, #4e9130 0%, #1e884d 100%) !important;
  color: white !important;
  border: none !important;
  border-radius: 12px !important;
  padding: 12px 24px !important;
  font-weight: 600 !important;
  text-transform: none !important;
  box-shadow: 0 4px 15px rgba(78, 145, 48, 0.3) !important;
  transition: all 0.3s ease !important;
}

.btn-add-modern:hover {
  transform: translateY(-2px) !important;
  box-shadow: 0 6px 20px rgba(78, 145, 48, 0.4) !important;
}

/* Content Section */
.classifications-content {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

/* Filters Section */
.filters-section {
  margin-bottom: 8px;
}

.filters-card {
  background: white;
  border-radius: 16px;
  padding: 24px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.2);
}

.filters-grid {
  display: grid;
  grid-template-columns: 250px 1fr;
  gap: 24px;
}

.filter-group {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.filter-group.search-group {
  max-width: 100%;
}

.filter-label {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  font-weight: 600;
  color: #374151;
  margin-bottom: 4px;
}

.filter-icon {
  color: #6b7280;
  font-size: 14px;
}

.modern-form-input,
.modern-form-select {
  border: 2px solid #e5e7eb !important;
  border-radius: 8px !important;
  padding: 12px 16px !important;
  font-size: 14px !important;
  transition: all 0.3s ease !important;
  background: white !important;
  font-family: "WorkSans-Regular", sans-serif !important;
}

.modern-form-input:focus,
.modern-form-select:focus {
  border-color: #0f65b0 !important;
  box-shadow: 0 0 0 3px rgba(15, 101, 176, 0.1) !important;
  outline: none !important;
}

/* Table Section */
.table-section {
  margin-bottom: 32px;
}

.table-card {
  background: white;
  border-radius: 16px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.2);
  overflow: hidden;
}

.loading-overlay {
  border-radius: 16px;
  min-height: 300px;
}

/* No Results State */
.no-results {
  text-align: center;
  padding: 80px 24px;
}

.no-results-icon {
  font-size: 64px;
  color: #cbd5e1;
  margin-bottom: 24px;
}

.no-results-title {
  color: #1e293b;
  font-size: 24px;
  font-weight: 600;
  margin: 0 0 12px 0;
}

.no-results-text {
  color: #64748b;
  font-size: 16px;
  margin: 0;
  max-width: 500px;
  margin-left: auto;
  margin-right: auto;
}

/* Table Styling */
.modern-table {
  background: transparent !important;
}

.name-cell {
  padding: 8px 0;
}

.name-text {
  font-weight: 500;
  color: #1e293b;
  font-size: 14px;
}

/* Status Badge */
.status-badge {
  display: inline-flex;
  align-items: center;
  padding: 6px 12px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.status-active {
  background: linear-gradient(135deg, #d1fae5 0%, #a7f3d0 100%);
  color: #059669;
  border: 1px solid #34d399;
}

.status-inactive {
  background: linear-gradient(135deg, #fee2e2 0%, #fecaca 100%);
  color: #dc2626;
  border: 1px solid #f87171;
}

/* Action Button */
.btn-action-edit {
  background: linear-gradient(135deg, #1d75be 0%, #155a8a 100%) !important;
  color: white !important;
  border: none !important;
  border-radius: 8px !important;
  font-weight: 600 !important;
  text-transform: none !important;
  font-size: 12px !important;
  box-shadow: 0 2px 8px rgba(29, 117, 190, 0.3) !important;
  transition: all 0.3s ease !important;
}

.btn-action-edit:hover {
  transform: translateY(-1px) !important;
  box-shadow: 0 4px 12px rgba(29, 117, 190, 0.4) !important;
}

/* Responsive Design */
@media (max-width: 1024px) {
  .header-content {
    flex-direction: column;
    gap: 16px;
    align-items: stretch;
    text-align: center;
  }

  .page-title {
    justify-content: center;
  }

  .filters-grid {
    grid-template-columns: 1fr;
    gap: 16px;
  }
}

@media (max-width: 768px) {
  .classifications-container {
    padding: 0 16px;
  }

  .classifications-header {
    padding: 16px 0;
    margin-bottom: 24px;
  }

  .header-content {
    padding: 0 16px;
  }

  .page-title {
    font-size: 24px;
  }

  .page-subtitle {
    font-size: 14px;
  }

  .filters-card,
  .table-card {
    border-radius: 12px;
    padding: 16px;
  }

  .no-results {
    padding: 60px 16px;
  }

  .no-results-icon {
    font-size: 48px;
  }

  .no-results-title {
    font-size: 20px;
  }

  .no-results-text {
    font-size: 14px;
  }
}

@media (max-width: 480px) {
  .btn-add-modern {
    padding: 10px 16px !important;
    font-size: 14px !important;
    width: 100%;
  }

  .page-title {
    font-size: 20px;
  }

  .filters-card {
    padding: 12px;
  }

  .filter-group {
    gap: 6px;
  }

  .status-badge {
    font-size: 11px;
    padding: 4px 10px;
  }
}

/* Override Vuetify styles */
.modern-table >>> .v-data-table__wrapper {
  border-radius: 0 !important;
}

.modern-table >>> .v-data-table thead th {
  background-color: #f8fafc !important;
  color: #374151 !important;
  font-weight: 600 !important;
  font-size: 12px !important;
  text-transform: uppercase !important;
  letter-spacing: 0.5px !important;
  border-bottom: 2px solid #e5e7eb !important;
}

.modern-table >>> .v-data-table tbody tr:hover {
  background-color: #f8fafc !important;
}

.modern-table >>> .v-data-table tbody tr {
  transition: all 0.2s ease !important;
}

.modern-table >>> .v-data-footer {
  border-top: 2px solid #e5e7eb !important;
  background-color: #f8fafc !important;
}
</style>